import { pence } from '@cf/shared';
export function amortiseYear(openingBalance, annualRateBps, monthlyPayment, interestOnly, annualOverpayment = 0) {
    if (!Number.isInteger(annualRateBps) || annualRateBps < 0) {
        throw new RangeError(`Interest rate must be non-negative integer bps: ${annualRateBps}`);
    }
    if (!Number.isSafeInteger(openingBalance) || openingBalance < 0) {
        throw new RangeError('Opening balance must be a non-negative integer of pence');
    }
    if (!Number.isSafeInteger(monthlyPayment) || monthlyPayment < 0) {
        throw new RangeError('Monthly payment must be a non-negative integer of pence');
    }
    if (!Number.isSafeInteger(annualOverpayment) || annualOverpayment < 0) {
        throw new RangeError('Annual overpayment must be a non-negative integer of pence');
    }
    let balance = openingBalance;
    let interestAccrued = 0;
    let totalPaid = 0;
    for (let month = 0; month < 12 && balance > 0; month += 1) {
        const interest = Math.trunc((balance * annualRateBps) / 120_000);
        interestAccrued += interest;
        if (interestOnly) {
            // Payments service the interest; principal is untouched. Any payment
            // above the interest is not applied (that is what interest-only means);
            // any shortfall capitalises onto the balance.
            const paid = Math.min(monthlyPayment, interest);
            totalPaid += paid;
            balance += interest - paid;
        }
        else {
            balance += interest;
            const paid = Math.min(monthlyPayment, balance);
            totalPaid += paid;
            balance -= paid;
        }
    }
    // Overpayment: extra principal at year end, capped at what remains. Applies
    // to interest-only debts too (a deliberate capital repayment).
    if (annualOverpayment > 0 && balance > 0) {
        const extra = Math.min(annualOverpayment, balance);
        balance -= extra;
        totalPaid += extra;
    }
    return Object.freeze({
        interestAccrued: pence(interestAccrued),
        totalPaid: pence(totalPaid),
        closingBalance: pence(Math.max(0, balance)),
    });
}
/**
 * The level monthly payment that clears `balance` over `termYears` at
 * `annualRateBps` — the standard annuity payment, and the figure a lender
 * quotes. Used to prefill the payment box in the fact-find so the adviser
 * confirms a calculated figure instead of typing one from memory (W1-10).
 *
 * Matches `amortiseYear`'s convention exactly (annual rate / 12, monthly
 * accrual), so the suggested payment actually clears the debt when the engine
 * runs it forward — asserted by round-tripping through `amortiseYear` in the
 * tests rather than by trusting the closed form.
 *
 * The rate factor is computed in floating point (a power of a fraction has no
 * exact integer form), but the RESULT is rounded to whole pence and returned as
 * `Pence`, so no fractional monetary value is ever produced or stored.
 * Returns null where no meaningful payment exists (nil balance or nil term).
 */
export function levelMonthlyPayment(balance, annualRateBps, termYears, interestOnly = false) {
    if (!Number.isSafeInteger(balance) || balance <= 0)
        return null;
    if (!Number.isInteger(annualRateBps) || annualRateBps < 0)
        return null;
    if (!Number.isFinite(termYears) || termYears <= 0)
        return null;
    const monthlyRate = annualRateBps / 10_000 / 12;
    // Interest-only: the payment covers the interest and nothing else, so the
    // balance is still outstanding at the end of the term.
    if (interestOnly)
        return pence(Math.round(balance * monthlyRate));
    const months = Math.round(termYears * 12);
    if (months <= 0)
        return null;
    // A 0% debt is simply the balance spread evenly — the annuity formula
    // divides by zero here.
    if (monthlyRate === 0)
        return pence(Math.ceil(balance / months));
    const factor = Math.pow(1 + monthlyRate, -months);
    const payment = (balance * monthlyRate) / (1 - factor);
    if (!Number.isFinite(payment))
        return null;
    // Rounded UP: a payment rounded down leaves a residual balance the schedule
    // never clears, which is precisely the kind of 1p tail that has previously
    // shown up as a phantom shortfall.
    return pence(Math.ceil(payment));
}
//# sourceMappingURL=amortise.js.map